import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Animated } from 'react-native';
import {
  PanGestureHandler,
  State,
  PanGestureHandlerGestureEvent,
} from 'react-native-gesture-handler';
import type {
  SmartNotification,
  AINotificationType,
} from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { styles } from './SmartNotification.styles';

interface SmartNotificationProps {
  notification: SmartNotification;
  onPress: () => void;
  onDismiss: () => void;
  showAIReasoning?: boolean;
}

export const SmartNotificationComponent: React.FC<SmartNotificationProps> = ({
  notification,
  onPress,
  onDismiss,
  showAIReasoning = false,
}) => {
  const [showReasoning, setShowReasoning] = useState(false);
  const translateX = new Animated.Value(0);
  const opacity = new Animated.Value(1);

  // Get notification type styling
  const getNotificationTypeStyle = (type: AINotificationType) => {
    switch (type) {
      case AINotificationType.MILESTONE_ACHIEVEMENT:
      case AINotificationType.CELEBRATION:
        return styles.celebrationNotification;
      case AINotificationType.STREAK_REMINDER:
      case AINotificationType.ACTIVITY_REMINDER:
        return styles.reminderNotification;
      case AINotificationType.NEW_RECOMMENDATION:
      case AINotificationType.CONTENT_SUGGESTION:
        return styles.recommendationNotification;
      case AINotificationType.SKILL_MASTERY:
        return styles.masteryNotification;
      case AINotificationType.JOURNEY_ADAPTATION:
        return styles.adaptationNotification;
      case AINotificationType.SOCIAL_ACTIVITY:
        return styles.socialNotification;
      case AINotificationType.RE_ENGAGEMENT:
        return styles.reEngagementNotification;
      default:
        return styles.defaultNotification;
    }
  };

  // Get notification icon
  const getNotificationIcon = (type: AINotificationType) => {
    switch (type) {
      case AINotificationType.MILESTONE_ACHIEVEMENT:
        return '🏆';
      case AINotificationType.CELEBRATION:
        return '🎉';
      case AINotificationType.STREAK_REMINDER:
        return '🔥';
      case AINotificationType.ACTIVITY_REMINDER:
        return '⏰';
      case AINotificationType.NEW_RECOMMENDATION:
        return '💡';
      case AINotificationType.CONTENT_SUGGESTION:
        return '📚';
      case AINotificationType.SKILL_MASTERY:
        return '⭐';
      case AINotificationType.JOURNEY_ADAPTATION:
        return '🎯';
      case AINotificationType.SOCIAL_ACTIVITY:
        return '👥';
      case AINotificationType.RE_ENGAGEMENT:
        return '🚀';
      default:
        return '📱';
    }
  };

  // Get priority indicator color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#E74C3C';
      case 'medium':
        return '#F39C12';
      case 'low':
        return '#95A5A6';
      default:
        return '#95A5A6';
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Handle swipe gesture
  const onGestureEvent = (event: PanGestureHandlerGestureEvent) => {
    const { translationX } = event.nativeEvent;
    translateX.setValue(translationX);
  };

  const onHandlerStateChange = (event: PanGestureHandlerGestureEvent) => {
    const { state, translationX } = event.nativeEvent;

    if (state === State.END) {
      if (Math.abs(translationX) > 100) {
        // Swipe to dismiss
        Animated.parallel([
          Animated.timing(translateX, {
            toValue: translationX > 0 ? 300 : -300,
            duration: 200,
            useNativeDriver: true,
          }),
          Animated.timing(opacity, {
            toValue: 0,
            duration: 200,
            useNativeDriver: true,
          }),
        ]).start(() => {
          onDismiss();
        });
      } else {
        // Snap back
        Animated.spring(translateX, {
          toValue: 0,
          useNativeDriver: true,
        }).start();
      }
    }
  };

  return (
    <PanGestureHandler
      onGestureEvent={onGestureEvent}
      onHandlerStateChange={onHandlerStateChange}
    >
      <Animated.View
        style={[
          styles.container,
          getNotificationTypeStyle(notification.type),
          {
            transform: [{ translateX }],
            opacity,
          },
        ]}
      >
        <TouchableOpacity
          style={styles.touchable}
          onPress={onPress}
          activeOpacity={0.7}
        >
          {/* Priority Indicator */}
          <View
            style={[
              styles.priorityIndicator,
              { backgroundColor: getPriorityColor(notification.priority) },
            ]}
          />

          {/* Main Content */}
          <View style={styles.content}>
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <Text style={styles.icon}>
                  {getNotificationIcon(notification.type)}
                </Text>
                <View style={styles.titleContainer}>
                  <Text style={styles.title} numberOfLines={1}>
                    {notification.title}
                  </Text>
                  <Text style={styles.timestamp}>
                    {formatTimeAgo(notification.scheduledFor)}
                  </Text>
                </View>
              </View>
              <View style={styles.headerRight}>
                <ConfidenceIndicator
                  confidence={notification.confidence}
                  size='small'
                  showLabel={false}
                />
                <TouchableOpacity
                  style={styles.dismissButton}
                  onPress={onDismiss}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Text style={styles.dismissButtonText}>×</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Content */}
            <Text style={styles.contentText} numberOfLines={3}>
              {notification.content}
            </Text>

            {/* AI Reasoning Toggle */}
            {(showAIReasoning || notification.priority === 'high') && (
              <TouchableOpacity
                style={styles.reasoningToggle}
                onPress={() => setShowReasoning(!showReasoning)}
              >
                <Text style={styles.reasoningToggleText}>
                  {showReasoning
                    ? '🤖 Hide AI reasoning'
                    : '🤖 Why this notification?'}
                </Text>
              </TouchableOpacity>
            )}

            {/* AI Reasoning */}
            {showReasoning && (
              <View style={styles.reasoningContainer}>
                <Text style={styles.reasoningText}>
                  {notification.aiReasoning}
                </Text>
                {notification.personalizationFactors.length > 0 && (
                  <View style={styles.factorsContainer}>
                    <Text style={styles.factorsTitle}>
                      Personalization factors:
                    </Text>
                    {notification.personalizationFactors.map(
                      (factor, index) => (
                        <Text key={index} style={styles.factorText}>
                          • {factor}
                        </Text>
                      )
                    )}
                  </View>
                )}
              </View>
            )}

            {/* Metadata */}
            {notification.metadata?.actionRequired && (
              <View style={styles.actionContainer}>
                <Text style={styles.actionText}>
                  {notification.metadata.actionRequired}
                </Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </Animated.View>
    </PanGestureHandler>
  );
};
